import { useEffect, useMemo, useState } from 'react';
import { AxiosResponse } from 'axios';
import useQueryParams from './useQueryParams';
import { PaginatedData, PaginationParams } from '../models/Pagination';

type PaginatedRequestConfig<T> = {
  getItems: (
    params?: PaginationParams
  ) => Promise<AxiosResponse<PaginatedData<T>>>;
  defaultParams: PaginationParams;
  dependencies?: Array<any>;
};

export default function usePaginatedRequest<T>({
  getItems,
  defaultParams = {},
  dependencies = [],
}: PaginatedRequestConfig<T>) {
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<T[]>([]);

  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);

  const { params } = useQueryParams();

  const searchParams = useMemo(
    () => ({ ...defaultParams, ...params }),
    [defaultParams, params]
  );

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const {
          data: { data, totalDocuments, totalPages, currentPage },
        } = await getItems(searchParams);
        setPage(currentPage);
        setTotalItems(totalDocuments);
        setTotalPages(totalPages);
        setItems(data);
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies]);

  return { isLoading, items, totalPages, totalItems, page };
}
